import React, { useEffect, useState } from 'react';

import { ActionIcon, Box, Button, CopyButton, Flex, Modal, Text, Title, useMantineTheme } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import bgImage from 'assets/images/bg-dark.png';
import { Branding } from 'components/Branding';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useUserContext } from 'contexts/UserContext';
import { Tag01Icon } from 'hugeicons-react';
import Countdown from 'react-countdown';
import { useTranslation } from 'react-i18next';
import { SubscriptionService } from 'services/subscription-service';
import { UserService } from 'services/user-service';
import { BF_PROMO_END_DATE } from 'shared/constants/general-constants';
import { BillingSession } from 'shared/interfaces/CustomerPortalSession';
import { RecurringInterval } from 'shared/interfaces/Product';
import { UserAccess } from 'shared/interfaces/User';

const TEN_SECONDS = 10000;
const MONTHLY_PROMO_CODE = 'BF75';
const YEARLY_PROMO_CODE = 'BF30';

export const PROMO_SEEN_KEY = 'blackFridayPromoSeen';

export function BlackFridayModal() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [user] = useUserContext();
  const [subscription] = useSubscriptionContext();

  const [promoSeen, setPromoSeen] = useLocalStorage({ key: PROMO_SEEN_KEY, defaultValue: true });
  const [open, setOpen] = useState(false);

  const createBillingPortalSession = useMutation({
    mutationFn: () =>
      SubscriptionService.createBillingPortalSession({
        updateSubscription: true
      }),
    onSuccess: async (billingSession: BillingSession) => {
      setPromoSeen(true);
      window.location.href = billingSession.url;
      await new Promise((resolve) => setTimeout(resolve, TEN_SECONDS));
    }
  });

  useEffect(() => {
    if (new Date(BF_PROMO_END_DATE) < new Date()) return;
    if (
      !user._id ||
      promoSeen ||
      !UserService.hasUserAccessWithMinRole(UserAccess.SUPER_ADMIN) ||
      UserService.isCustomOrganisationMember()
    )
      return;

    setOpen(true);
  }, [user, promoSeen]);

  const handleClose = () => {
    setOpen(false);
    setPromoSeen(true);
  };

  const shouldHideMonthlyPromo =
    subscription.product.recurringInterval === RecurringInterval.YEAR || subscription.product.price > 0;
  const shouldHideYearlyPromo = subscription.product.recurringInterval === RecurringInterval.YEAR;

  return (
    <Modal
      size={475}
      opened={open}
      onClose={handleClose}
      title={<Branding width={110} isLight />}
      styles={{
        content: {
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          minWidth: 400,
          color: 'white'
        },
        body: {
          padding: 20,
          height: 400
        },
        header: { backgroundColor: '#2B2B2B' },
        close: { color: 'white' }
      }}>
      <Flex direction="column" justify="space-between" h="100%" w="100%">
        <Title align="center" sx={{ fontSize: '2.5rem', fontWeight: 600 }}>
          {t('BLACK_FRIDAY_PROMO.TITLE')}
        </Title>
        <Countdown
          date={BF_PROMO_END_DATE}
          renderer={({ days, hours, minutes, seconds }) => (
            <Flex direction="column" align="center" justify="center">
              <Text color={theme.other.text.dimmed2} mb={3}>
                {t('BLACK_FRIDAY_PROMO.EXPIRES_IN')}
              </Text>
              <Flex
                direction="column"
                align="center"
                justify="center"
                p="xs"
                bg="white"
                miw={200}
                sx={{ borderRadius: '10px' }}>
                <Title order={1} color={theme.other.text.default}>
                  {t('APP.COUNTDOWN', { days, hours, minutes, seconds })}
                </Title>
              </Flex>
            </Flex>
          )}
        />
        <Flex direction="column" gap={15} ml="xl">
          {!shouldHideMonthlyPromo && (
            <Flex direction="row" align="flex-start" gap="xs">
              <Tag01Icon style={{ marginTop: 2 }} />
              <Box>
                <Text sx={{ fontSize: '1.2rem', fontWeight: 400 }}>{t('BLACK_FRIDAY_PROMO.DESCRIPTION.ONE.TEXT')}</Text>
                <Flex direction="row" align="center" gap="xs">
                  <Text sx={{ fontSize: '1.2rem', fontWeight: 400 }}>
                    {t('BLACK_FRIDAY_PROMO.DESCRIPTION.ONE.CODE')}
                  </Text>
                  <CopyButton value={MONTHLY_PROMO_CODE} timeout={2000}>
                    {({ copied, copy }) => (
                      <ActionIcon onClick={copy} color="white">
                        {copied ? <IconCheck color="green" /> : <IconCopy color="white" />}
                      </ActionIcon>
                    )}
                  </CopyButton>
                </Flex>
              </Box>
            </Flex>
          )}
          {!shouldHideYearlyPromo && (
            <Flex direction="row" align="flex-start" gap="xs">
              <Tag01Icon style={{ marginTop: 2 }} />
              <Box>
                <Text sx={{ fontSize: '1.2rem', fontWeight: 400 }}>{t('BLACK_FRIDAY_PROMO.DESCRIPTION.TWO.TEXT')}</Text>
                <Flex direction="row" align="center" gap="xs">
                  <Text sx={{ fontSize: '1.2rem', fontWeight: 400 }}>
                    {t('BLACK_FRIDAY_PROMO.DESCRIPTION.TWO.CODE')}
                  </Text>
                  <CopyButton value={YEARLY_PROMO_CODE} timeout={2000}>
                    {({ copied, copy }) => (
                      <ActionIcon onClick={copy} color="white">
                        {copied ? <IconCheck color="green" /> : <IconCopy color="white" />}
                      </ActionIcon>
                    )}
                  </CopyButton>
                </Flex>
              </Box>
            </Flex>
          )}
        </Flex>
        <Button
          variant="default"
          w="100%"
          mt="md"
          loading={createBillingPortalSession.isLoading}
          onClick={() => createBillingPortalSession.mutate()}>
          {t('APP.ACTIONS.CLAIM_OFFER')}
        </Button>
      </Flex>
    </Modal>
  );
}
